'use client';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getSdkAuthStore } from '../../auth/state/auth-state.js';
import { useSdkNetworkScope, useSdkViewerScope } from '../../provider/SdkRuntimeContext.js';
import { canMaterializeViewerRelationshipCollection, materializeViewerRelationshipData, useViewerRelationshipIndexIds, useViewerRelationshipRow, viewerRelationshipIndexQueryKey, viewerRelationshipQueryKey, viewerRelationshipScope, viewerRelationshipTarget, writeViewerRelationship, writeViewerRelationshipCollection, } from '../internal/viewer-relationship.js';
import { createHook } from './createHook.js';
export function createViewerRelationshipHook(spec, realtime) {
    const factory = spec.entityKeys[spec.kind];
    const relationship = spec.viewerRelationship;
    if (factory === undefined || relationship === undefined) {
        throw new Error('Viewer relationship hook requires its relationship contract');
    }
    const responseBoundary = (data, context) => {
        writeResponseToViewerRelationship(context.queryClient, context.queryKey, viewerRelationshipScope(context.viewerScope, context.networkScope), relationship, context.args, data, context.viewerAccountId, { source: 'rest', observedAtMs: context.observedAtMs });
        return data;
    };
    const useBase = realtime === undefined
        ? createHook(spec, undefined, { responseBoundary })
        : createHook(spec, realtime, { responseBoundary });
    return function useViewerRelationshipHook(args, opts) {
        const result = useBase(args, opts);
        const queryClient = useQueryClient();
        const viewerScope = useSdkViewerScope();
        const networkScope = useSdkNetworkScope();
        const relationshipScope = viewerRelationshipScope(viewerScope, networkScope);
        const sourceQueryKey = factory(args).queryKey;
        const sourceQueryHash = hashKey(sourceQueryKey);
        const rawArgs = args;
        const targetId = viewerRelationshipTarget(relationship, rawArgs);
        const viewerAccountId = getSdkAuthStore().current().user?.id ?? null;
        const pointKey = targetId === undefined
            ? undefined
            : viewerRelationshipQueryKey(relationshipScope, relationship.kind, targetId);
        const collectionKey = relationship.collection === undefined ||
            !canMaterializeViewerRelationshipCollection(relationship, rawArgs, viewerAccountId)
            ? undefined
            : viewerRelationshipIndexQueryKey(relationshipScope, relationship.kind, sourceQueryKey);
        const relationshipRow = useViewerRelationshipRow(queryClient, pointKey);
        const relationshipIds = useViewerRelationshipIndexIds(queryClient, collectionKey);
        const sourceQueryKeyRef = useRef(sourceQueryKey);
        sourceQueryKeyRef.current = sourceQueryKey;
        const rawArgsRef = useRef(rawArgs);
        rawArgsRef.current = rawArgs;
        useEffect(() => {
            void sourceQueryHash;
            if (relationshipScope.length === 0)
                return;
            const currentSourceQueryKey = sourceQueryKeyRef.current;
            const data = queryClient.getQueryData(currentSourceQueryKey);
            if (data === undefined)
                return;
            writeResponseToViewerRelationship(queryClient, currentSourceQueryKey, relationshipScope, relationship, rawArgsRef.current, data, viewerAccountId, { source: 'hydrate', observedAtMs: 0 });
        }, [queryClient, relationshipScope, sourceQueryHash, viewerAccountId]);
        return new Proxy(result, {
            get(target, property, receiver) {
                if (property !== 'data')
                    return Reflect.get(target, property, receiver);
                void relationshipRow;
                void relationshipIds;
                return materializeViewerRelationshipData(queryClient, sourceQueryKey, relationshipScope, relationship, rawArgs, result.data);
            },
        });
    };
}
function writeResponseToViewerRelationship(queryClient, sourceQueryKey, viewerScope, relationship, args, data, viewerAccountId, meta) {
    if (viewerScope.length === 0)
        return;
    const targetId = viewerRelationshipTarget(relationship, args);
    if (relationship.point !== undefined && targetId !== undefined) {
        const value = data !== null && typeof data === 'object'
            ? data[relationship.point.valueField]
            : undefined;
        if (typeof value === 'boolean') {
            writeViewerRelationship(queryClient, viewerScope, relationship.kind, targetId, value, meta);
        }
        return;
    }
    if (relationship.collection !== undefined) {
        writeViewerRelationshipCollection(queryClient, sourceQueryKey, viewerScope, relationship, args, data, viewerAccountId, meta);
    }
}
//# sourceMappingURL=createViewerRelationshipHook.js.map